import { useEffect, useRef, type PropsWithChildren } from "react";

type MagnetProps = PropsWithChildren<{
  padding?: number;
  strength?: number;
  activeTransition?: string;
  inactiveTransition?: string;
  className?: string;
}>;

export function Magnet({
  children,
  padding = 150,
  strength = 3,
  activeTransition = "transform 0.3s ease-out",
  inactiveTransition = "transform 0.6s ease-in-out",
  className,
}: MagnetProps) {
  const hitAreaRef = useRef<HTMLDivElement>(null);
  const movingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hitArea = hitAreaRef.current;
    const movingElement = movingRef.current;
    if (!hitArea || !movingElement) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(pointer: fine)");
    if (reduceMotion.matches || !finePointer.matches) return;

    movingElement.style.willChange = "transform";

    let frame = 0;
    let latestEvent: PointerEvent | null = null;

    const renderPosition = () => {
      frame = 0;
      if (!latestEvent) return;

      const rect = hitArea.getBoundingClientRect();
      const deltaX = latestEvent.clientX - (rect.left + rect.width / 2);
      const deltaY = latestEvent.clientY - (rect.top + rect.height / 2);
      const active =
        Math.abs(deltaX) <= rect.width / 2 + padding &&
        Math.abs(deltaY) <= rect.height / 2 + padding;

      movingElement.style.transition = active ? activeTransition : inactiveTransition;
      movingElement.style.transform = active
        ? `translate3d(${deltaX / strength}px, ${deltaY / strength}px, 0)`
        : "translate3d(0, 0, 0)";
    };

    const handlePointerMove = (event: PointerEvent) => {
      latestEvent = event;
      if (!frame) frame = window.requestAnimationFrame(renderPosition);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      if (frame) window.cancelAnimationFrame(frame);
      movingElement.style.transform = "";
      movingElement.style.transition = "";
      movingElement.style.willChange = "";
    };
  }, [activeTransition, inactiveTransition, padding, strength]);

  return (
    <div ref={hitAreaRef} className={className} data-magnet="signal">
      <div ref={movingRef} className="h-full w-full">
        {children}
      </div>
    </div>
  );
}
